'use client';

import React, { ComponentPropsWithoutRef, ReactNode, useCallback, useState } from 'react';

import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';

import { Pagination, PaginationProps } from '@/blocks/components/Controls/Pagination';
import { PostCard, PostType, PostsGrid } from '@/blocks/components/PostCard';
import { siteVersion } from '@/global/public-settings';
import { useScrollTo } from '@/hooks/useScrollTo';
import buttons from '@/styles/button.module.scss';

import styles from './styles.module.scss';

const POSTS_PER_PAGE = 15;

type PAMPostsProps = {
  posts: PostType[];
  children?: ReactNode;
};

export const PAMPosts = ({ posts, children }: PAMPostsProps) => {
  const tr = useTranslations();
  const locale = useLocale();
  const tabs = [
    { label: tr.rich('pages.pressAndMedia.sections.posts.languageButtons.en'), value: 'en' },
    { label: tr.rich('pages.pressAndMedia.sections.posts.languageButtons.es'), value: 'es' },
    { label: tr.rich('pages.pressAndMedia.sections.posts.languageButtons.pt'), value: 'pt' },
    { label: tr.rich('pages.pressAndMedia.sections.posts.languageButtons.vi'), value: 'vi' },
    { label: tr.rich('pages.pressAndMedia.sections.posts.languageButtons.th'), value: 'th' },
    ...(siteVersion.isTurkey
      ? [{ label: tr.rich('pages.pressAndMedia.sections.posts.languageButtons.tr'), value: 'tr' }]
      : []),
  ];
  const tabsValues = tabs.map((tab) => tab.value);
  const [selectedLang, setSelectedLang] = useState(() => (tabsValues.includes(locale) ? locale : 'en'));
  const [selectedPage, setSelectedPage] = useState(1);
  const filteredByLang = posts.filter((post) => post.language === selectedLang);
  const startIndex = (selectedPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentPosts = filteredByLang.slice(startIndex, endIndex);

  const { scrollRef, scrollToElement } = useScrollTo();

  const onPaginationChange = useCallback<PaginationProps['onChange']>(
    (page) => {
      setSelectedPage(page);
      setTimeout(() => {
        scrollToElement();
      }, 0);
    },
    [scrollToElement],
  );
  return (
    <>
      <div className={classNames(styles.languageSelector)}>
        <div className={styles.languageButtons} ref={scrollRef}>
          {tabs.map((tab, i) => (
            <LanguageButton
              key={i}
              onClick={() => setSelectedLang(tab.value)}
              isActive={selectedLang === tab.value}
            >
              {tab.label}
            </LanguageButton>
          ))}
        </div>
      </div>
      <PostsGrid>
        {currentPosts.map((post, i) => {
          return <PostCard key={i} post={post} />;
        })}
      </PostsGrid>
      {children}
      <Pagination count={filteredByLang.length} perPage={POSTS_PER_PAGE} onChange={onPaginationChange} />
    </>
  );
};

interface LanguageButtonProps extends ComponentPropsWithoutRef<'button'> {
  children: ReactNode;
  isActive?: boolean;
}

const LanguageButton = ({ children, isActive, ...rest }: LanguageButtonProps) => {
  return (
    <button
      className={classNames(
        styles.languageButton,
        buttons.btn,
        buttons.btnPill,
        buttons.btnPillTransparent,
        buttons.btnM,
        {
          [buttons.btnPillActive]: isActive,
        },
      )}
      {...rest}
    >
      {children}
    </button>
  );
};
